// src/pages/Login.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    canSignInWithApple,
    needsVerification,
    refreshVerification,
    registerWithEmail,
    requestPasswordReset,
    resendVerification,
    signInWithApple,
    signInWithEmail,
    signInWithGoogle,
    signOutUser,
    subscribeToAuth,
    subscribeToPendingVerification,
} from '../auth';
import { AppleIcon, EyeIcon, EyeOffIcon, GoogleIcon, TrophyIcon } from '../components/icons';
import { startGuest } from '../guest';

// What to tell the user for the sign-in failures we can recognise (Firebase error codes).
export function signInErrorMessage(error) {
    switch (error?.code) {
        case 'auth/unauthorized-domain':
            return `Sign-in isn't enabled for this address (${window.location.hostname}) yet. Add it under Firebase Console > Authentication > Settings > Authorized domains.`;
        case 'auth/popup-blocked':
            return 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.';
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
            return 'The sign-in window was closed before finishing. Please try again.';
        case 'auth/network-request-failed':
            return 'Could not reach the sign-in service. Check your connection and try again.';
        case 'auth/native-timeout':
            return 'The sign-in did not answer. Please try again, or use another way to sign in.';
        case 'auth/operation-not-allowed':
            return 'Signing in with an email and password is not switched on yet (Firebase Console > Authentication > Sign-in method).';
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
            return 'That email and password do not match an account.';
        case 'auth/invalid-email':
            return 'That does not look like an email address.';
        case 'auth/email-already-in-use':
            return 'There is already an account with that email. Sign in instead, or reset the password.';
        case 'auth/weak-password':
            return 'Please choose a longer password (at least 6 characters).';
        case 'auth/too-many-requests':
            return 'Too many attempts. Wait a moment and try again.';
        case 'auth/user-token-expired':
            return 'Please sign in again.';
        case 'auth/missing-password':
            return 'Enter your password.';
        default:
            return 'Sign-in did not complete. Please try again.';
    }
}

const COOLDOWN_MS = 60 * 1000;

function Login() {
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const [signingIn, setSigningIn] = useState(false);
    const [withEmail, setWithEmail] = useState(false);
    const [creating, setCreating] = useState(false); // making a new account instead of signing in
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [notice, setNotice] = useState(null);
    const [unconfirmed, setUnconfirmed] = useState(null); // the user who still has to confirm their address
    const [cooldownUntil, setCooldownUntil] = useState(0); // no new confirmation email before this time (Firebase limits them)
    const [now, setNow] = useState(Date.now());
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = subscribeToAuth((firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);
                navigate('/dashboard');
            } else {
                setUser(null);
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    useEffect(() => subscribeToPendingVerification(setUnconfirmed), []);

    const handleAppleSignIn = async () => {
        setError(null);
        setSigningIn(true);
        try {
            await signInWithApple();
            navigate('/dashboard');
        } catch (signInError) {
            console.error('Apple Sign-In error:', signInError);
            setError(signInErrorMessage(signInError));
            setSigningIn(false);
        }
    };

    // counts down the wait before another email may be asked for
    useEffect(() => {
        if (cooldownUntil <= Date.now()) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [cooldownUntil]);

    const handleGoogleSignIn = async () => {
        setError(null);
        setSigningIn(true);
        try {
            await signInWithGoogle();
            navigate('/dashboard');
        } catch (signInError) {
            console.error('Google Sign-In error:', signInError);
            setError(signInErrorMessage(signInError));
            setSigningIn(false);
        }
    };

    const handleEmailSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setNotice(null);
        setSigningIn(true);
        try {
            const { user: signedIn } = await (creating ? registerWithEmail(email, password) : signInWithEmail(email, password));
            if (needsVerification(signedIn)) {
                if (creating) {
                    setCooldownUntil(Date.now() + COOLDOWN_MS); // registering has just sent the email
                    setNow(Date.now());
                }
                setSigningIn(false); // the screen below asks them to confirm the address first
                return;
            }
            navigate('/dashboard');
        } catch (signInError) {
            console.error('Email sign-in error:', signInError);
            setError(signInErrorMessage(signInError));
            setSigningIn(false);
        }
    };

    const handleReset = async () => {
        setError(null);
        setNotice(null);
        if (!email.trim()) {
            setError('Enter your email address first.');
            return;
        }
        try {
            await requestPasswordReset(email);
        } catch (resetError) {
            // (whether or not there is such an account is not revealed: a missing one is not an error)
            if (resetError?.code !== 'auth/user-not-found') {
                console.error('Password reset error:', resetError);
                setError(signInErrorMessage(resetError));
                return;
            }
        }
        setNotice(
            'If there is an account with that email, a link to choose a new password is on its way. ' +
                'It comes from noreply@tournamental.app and may take a few minutes; check the spam folder too.'
        );
    };

    const waitSeconds = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

    const checkVerified = async () => {
        setError(null);
        setNotice(null);
        try {
            if (!(await refreshVerification())) setNotice('Not confirmed yet. Open the link in the email, then try again.');
        } catch (verifyError) {
            console.error('Could not check the address:', verifyError);
            setError(signInErrorMessage(verifyError));
        }
    };

    const sendAgain = async () => {
        setError(null);
        setNotice(null);
        try {
            await resendVerification();
            setCooldownUntil(Date.now() + COOLDOWN_MS);
            setNow(Date.now());
            setNotice('Sent again. It may take a few minutes; check the spam folder too.');
        } catch (sendError) {
            console.error('Could not send the email:', sendError);
            if (sendError?.code === 'auth/too-many-requests') {
                // (an email went out a moment ago, when registering or asking before)
                setCooldownUntil(Date.now() + 2 * COOLDOWN_MS);
                setNow(Date.now());
                setNotice('An email was sent a moment ago. Give it a few minutes before asking again.');
            } else {
                setError(signInErrorMessage(sendError));
            }
        }
    };

    if (user) {
        return null;
    }

    if (unconfirmed) {
        return (
            <div className="login-page">
                <div className="login-card card">
                    <div className="brand-mark">
                        <TrophyIcon size={28} />
                    </div>
                    <h1>Confirm your email</h1>
                    <p className="muted">
                        We sent a link to <strong>{unconfirmed.email}</strong>. Open it to confirm that the address is yours, then
                        come back here. It comes from noreply@tournamental.app; check the spam folder too.
                    </p>
                    <div className="verify-actions">
                        <button className="btn btn-primary" onClick={checkVerified}>
                            I have confirmed it
                        </button>
                        <button className="btn" onClick={sendAgain} disabled={waitSeconds > 0}>
                            {waitSeconds > 0 ? `Send the email again (in ${waitSeconds} s)` : 'Send the email again'}
                        </button>
                        <button className="btn btn-ghost" onClick={() => signOutUser()}>
                            Use a different account
                        </button>
                    </div>
                    <p className="hint">
                        Nothing after a few minutes? Check spam, then try again later, use another address, or sign in with Google
                        or Apple.
                    </p>
                    {notice && (
                        <p className="hint" role="status">
                            {notice}
                        </p>
                    )}
                    {error && (
                        <p className="form-error" role="alert">
                            {error}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-card card">
                <div className="brand-mark">
                    <TrophyIcon size={28} />
                </div>
                <h1>Tournamental</h1>
                <p className="muted">Please sign in to manage your tournaments.</p>
                <button className="btn btn-google" onClick={handleGoogleSignIn} disabled={signingIn}>
                    <GoogleIcon />
                    Sign in with Google
                </button>
                {canSignInWithApple() && (
                    <button className="btn btn-apple" onClick={handleAppleSignIn} disabled={signingIn}>
                        <AppleIcon /> Sign in with Apple
                    </button>
                )}
                {withEmail ? (
                    <form className="email-form" onSubmit={handleEmailSubmit}>
                        <input
                            className="input"
                            type="email"
                            autoComplete="email"
                            placeholder="Email"
                            aria-label="Email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                        />
                        <div className="password-field">
                            <input
                                className="input"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete={creating ? 'new-password' : 'current-password'}
                                placeholder="Password"
                                aria-label="Password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="btn btn-ghost btn-icon password-toggle"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                aria-pressed={showPassword}
                                onMouseDown={(event) => event.preventDefault()} // keeps the keyboard and cursor where they were
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </div>
                        <button className="btn btn-primary" type="submit" disabled={signingIn}>
                            {creating ? 'Create account' : 'Sign in'}
                        </button>
                        <p className="hint">
                            <button type="button" className="link-button" onClick={() => setCreating(!creating)}>
                                {creating ? 'I already have an account' : 'Create an account'}
                            </button>
                            {!creating && (
                                <>
                                    {' · '}
                                    <button type="button" className="link-button" onClick={handleReset}>
                                        Forgot password?
                                    </button>
                                </>
                            )}
                        </p>
                    </form>
                ) : (
                    <button className="btn" onClick={() => setWithEmail(true)} disabled={signingIn}>
                        Sign in with email
                    </button>
                )}
                <button className="btn btn-ghost" onClick={() => startGuest()} disabled={signingIn}>
                    Continue without an account
                </button>
                <p className="hint">
                    Without an account your tournaments stay on this device only. You can sign in later and move them into
                    your account.
                </p>
                {notice && (
                    <p className="hint" role="status">
                        {notice}
                    </p>
                )}
                {error && (
                    <p className="form-error" role="alert">
                        {error}
                    </p>
                )}
            </div>
        </div>
    );
}

export default Login;
